'use client'

import { createContext, useContext, type ReactNode } from 'react'
import { generalMedicineAuthCopy, type AuthCopy } from '@/lib/content/auth'
import { generalMedicineInsuranceCopy, type InsuranceCopy } from '@/lib/content/insurance'
import { generalMedicineQuestionnaireCopy, type QuestionnaireCopy } from '@/lib/content/questionnaire'
import { generalMedicineVisitCopy, type VisitCopy } from '@/lib/content/visit'
import { generalMedicineLabsCopy, type LabsCopy } from '@/lib/content/labs'
import { generalMedicineLocationCopy, type LocationCopy } from '@/lib/content/location'
import { generalMedicineCarePlanCopy, type CarePlanCopy } from '@/lib/content/carePlan'

export interface AppCopy {
    auth: AuthCopy
    insurance: InsuranceCopy
    questionnaire: QuestionnaireCopy
    visit: VisitCopy
    labs: LabsCopy
    location: LocationCopy
    carePlan: CarePlanCopy
}

const DEFAULT_COPY: AppCopy = {
    auth: generalMedicineAuthCopy,
    insurance: generalMedicineInsuranceCopy,
    questionnaire: generalMedicineQuestionnaireCopy,
    visit: generalMedicineVisitCopy,
    labs: generalMedicineLabsCopy,
    location: generalMedicineLocationCopy,
    carePlan: generalMedicineCarePlanCopy,
}

const CopyContext = createContext<AppCopy>(DEFAULT_COPY)

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] }

function deepMerge<T>(base: T, override: DeepPartial<T> | undefined): T {
    if (!override) return base
    const out = { ...base }
    for (const key of Object.keys(override) as (keyof T)[]) {
        const next = override[key]
        if (next === undefined) continue
        const current = base[key]
        out[key] =
            current && typeof current === 'object' && !Array.isArray(current)
                ? deepMerge(current, next as DeepPartial<T[keyof T]>)
                : (next as T[keyof T])
    }
    return out
}

export function CopyProvider({ value, children }: { value: DeepPartial<AppCopy>; children: ReactNode }) {
    return <CopyContext.Provider value={deepMerge(DEFAULT_COPY, value)}>{children}</CopyContext.Provider>
}

export function useCopy(): AppCopy {
    return useContext(CopyContext)
}
